import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Loader2, RefreshCw, Search, Trash2, Pencil, X, Bluetooth, Radio,
  Bus, User, Map, ArrowRight, CheckCircle2, PauseCircle, Database,
} from "lucide-react";

const STATUS_LABELS = {
  active: { bg: "bg-emerald-500", text: "text-white", label: "Active" },
  inactive: { bg: "bg-zinc-200", text: "text-zinc-900", label: "Inactive" },
  completed: { bg: "bg-zinc-900", text: "text-white", label: "Terminée" },
};

function StatusPill({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.inactive;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

export default function Affectations() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, v, d, t, tg] = await Promise.all([
        api.get("/logibus/affectations"),
        api.get("/vehicles"),
        api.get("/users?role=driver&active_only=true").catch(() => ({ data: [] })),
        api.get("/gtfs/trips"),
        api.get("/logibus/bluetooth-tags").catch(() => ({ data: [] })),
      ]);
      setItems(a.data || []);
      setVehicles(v.data || []);
      setDrivers(d.data || []);
      setTrips(t.data || []);
      setTags(tg.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!q) return true;
      const blob = [
        a.vehicle?.plate, a.vehicle?.model, a.vehicle?.navixy_tracker_id,
        a.driver?.name, a.trip?.route_short_name, a.trip?.trip_headsign, a.label,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(q);
    });
  }, [items, search, statusFilter]);

  const counts = useMemo(() => ({
    total: items.length,
    active: items.filter((a) => a.status === "active").length,
    inactive: items.filter((a) => a.status === "inactive").length,
    with_tag: items.filter((a) => a.bluetooth_tag_id).length,
  }), [items]);

  const tagById = useMemo(() => Object.fromEntries(tags.map((t) => [t.id, t])), [tags]);

  const handleMigrate = async () => {
    if (!confirm("Importer les associations existantes Mapping flotte vers Affectations ?")) return;
    try {
      const { data } = await api.post("/logibus/affectations/migrate-from-mappings");
      toast.success(`${data.created} affectation(s) créée(s), ${data.skipped} déjà à jour`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const handleBackfillDrivers = async () => {
    // Phase 1 — dry run, show counts before applying
    let preview;
    try {
      const { data } = await api.post("/logibus/affectations/backfill-drivers?dry_run=true");
      preview = data;
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); return; }
    const lines = [
      `Aperçu (dry-run) :`,
      `• Affectations sans chauffeur : ${preview.matched}`,
      `• Pourront être backfillées   : ${(preview.items || []).filter(i => i.driver_id).length}`,
      `• Ignorées                    : ${preview.skipped}`,
      ``, `Appliquer le backfill maintenant ?`,
    ].join("\n");
    if (!confirm(lines)) return;
    try {
      const { data } = await api.post("/logibus/affectations/backfill-drivers?dry_run=false");
      toast.success(`${data.backfilled} affectation(s) mise(s) à jour, ${data.skipped} ignorée(s)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const handleDelete = async (a) => {
    if (!confirm(`Supprimer cette affectation ${a.vehicle?.plate} → ${a.trip?.route_short_name} ?`)) return;
    try {
      await api.delete(`/logibus/affectations/${a.id}`);
      toast.success("Affectation supprimée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const handleToggle = async (a) => {
    try {
      const newStatus = a.status === "active" ? "inactive" : "active";
      await api.put(`/logibus/affectations/${a.id}`, { status: newStatus });
      toast.success(`Affectation ${newStatus === "active" ? "réactivée" : "désactivée"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-4 lg:p-6" data-testid="affectations-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Source de vérité</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Affectations</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Bus → Ligne → Chauffeur → Tag Bluetooth. Une seule affectation active par bus / chauffeur / tag.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleMigrate} data-testid="migrate-btn"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <Database size={14} /> Importer mapping flotte
          </button>
          <button onClick={handleBackfillDrivers} data-testid="backfill-drivers-btn"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <Database size={14} /> Backfill chauffeurs
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="create-affectation-btn"
            className="inline-flex items-center gap-2 bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800">
            <Plus size={14} /> Nouvelle affectation
          </button>
          <button onClick={load} disabled={loading} data-testid="refresh-btn"
            className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#ffd43a]">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rafraîchir
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Kpi label="Total" value={counts.total} icon={Database} />
        <Kpi label="Actives" value={counts.active} icon={CheckCircle2} tone="emerald" />
        <Kpi label="Inactives" value={counts.inactive} icon={PauseCircle} tone="zinc" />
        <Kpi label="Avec tag BLE" value={counts.with_tag} icon={Bluetooth} tone="amber" />
      </div>

      <div className="border border-zinc-900">
        <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white flex items-center gap-2 flex-wrap">
          <Search size={12} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrer bus, ligne, chauffeur, tag…"
            data-testid="search-input" className="flex-1 min-w-[200px] bg-transparent outline-none text-xs font-mono text-white placeholder:text-zinc-500" />
          <div className="flex gap-1">
            {["all", "active", "inactive", "completed"].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/30 ${statusFilter === s ? "bg-[#FFC60A] text-zinc-900 border-[#FFC60A]" : "text-zinc-300 hover:bg-zinc-800"}`}>
                {s === "all" ? "Toutes" : STATUS_LABELS[s]?.label || s}
              </button>
            ))}
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{filtered.length}</span>
        </div>

        <div className="hidden lg:grid grid-cols-12 gap-2 px-3 py-2 border-b border-zinc-900 bg-zinc-50 text-[10px] font-mono tracking-widest uppercase text-zinc-500">
          <div className="col-span-3">Bus</div>
          <div className="col-span-2">Ligne</div>
          <div className="col-span-2">Chauffeur</div>
          <div className="col-span-2">Tag BLE</div>
          <div className="col-span-1">Statut</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {filtered.length === 0 && (
          <div className="p-8 text-xs text-zinc-500 text-center font-mono">
            Aucune affectation. Cliquez sur « Importer mapping flotte » pour récupérer les associations existantes
            ou créez-en une nouvelle.
          </div>
        )}

        <div className="divide-y divide-zinc-200">
          {filtered.map((a) => {
            const tag = a.bluetooth_tag_id ? tagById[a.bluetooth_tag_id] : null;
            return (
              <div key={a.id} data-testid={`affectation-${a.id}`}
                className="grid grid-cols-12 gap-2 px-3 py-3 items-center hover:bg-zinc-50">
                <div className="col-span-12 lg:col-span-3 min-w-0">
                  <div className="font-display font-bold text-sm tracking-tight truncate flex items-center gap-2">
                    <Bus size={13} className="text-zinc-500" /> {a.vehicle?.plate || "—"}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">{a.vehicle?.model || ""}</div>
                  {a.vehicle?.navixy_tracker_id && (() => {
                    const tid = String(a.vehicle.navixy_tracker_id);
                    const isSim = tid.startsWith("sim-");
                    return (
                      <div className="text-[10px] font-mono text-zinc-400 flex items-center gap-1">
                        {isSim ? (
                          <>
                            <span className="px-1 py-0.5 text-[8px] uppercase tracking-widest bg-orange-100 text-orange-800 border border-orange-300">DÉMO</span>
                            <span>Traceur simulé</span>
                          </>
                        ) : (
                          <>
                            <span className="px-1 py-0.5 text-[8px] uppercase tracking-widest bg-green-100 text-green-800 border border-green-300">GPS</span>
                            <span>Traceur GPS lié</span>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="col-span-6 lg:col-span-2 min-w-0">
                  {a.trip ? (
                    <>
                      <div className="font-display font-bold text-sm">{a.trip.route_short_name || a.trip.trip_id}</div>
                      <div className="text-[10px] font-mono text-zinc-500 truncate">→ {a.trip.trip_headsign}</div>
                    </>
                  ) : <span className="text-[10px] font-mono text-zinc-400">—</span>}
                </div>
                <div className="col-span-6 lg:col-span-2 min-w-0 text-xs">
                  {a.driver ? (
                    <span className="inline-flex items-center gap-1 truncate"><User size={11} className="text-zinc-500" /> {a.driver.name}</span>
                  ) : <span className="text-[10px] font-mono text-zinc-400">Non assigné</span>}
                </div>
                <div className="col-span-6 lg:col-span-2 min-w-0">
                  {tag ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono">
                      <Bluetooth size={11} className={tag.status === "connected" ? "text-emerald-600" : "text-zinc-400"} />
                      <span className="truncate">{tag.name}</span>
                    </span>
                  ) : a.bluetooth_tag_id ? (
                    <span className="text-[10px] font-mono text-zinc-400">tag inconnu</span>
                  ) : (
                    <span className="text-[10px] font-mono text-zinc-400">—</span>
                  )}
                </div>
                <div className="col-span-3 lg:col-span-1"><StatusPill status={a.status} /></div>
                <div className="col-span-3 lg:col-span-2 flex justify-end gap-1">
                  <Link to="/admin/live-tracking" title="Voir en live" data-testid={`view-live-${a.id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-zinc-900 hover:text-white">
                    <Radio size={11} />
                  </Link>
                  <button onClick={() => handleToggle(a)} title={a.status === "active" ? "Désactiver" : "Activer"} data-testid={`toggle-${a.id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-zinc-900 hover:text-white">
                    {a.status === "active" ? <PauseCircle size={11} /> : <CheckCircle2 size={11} />}
                  </button>
                  <button onClick={() => { setEditing(a); setShowForm(true); }} title="Modifier" data-testid={`edit-${a.id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-zinc-900 hover:text-white">
                    <Pencil size={11} />
                  </button>
                  <button onClick={() => handleDelete(a)} title="Supprimer" data-testid={`delete-${a.id}`}
                    className="border border-zinc-900 px-2 py-1 text-[10px] font-bold hover:bg-red-500 hover:text-white">
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <AffectationForm
          affectation={editing}
          vehicles={vehicles} drivers={drivers} trips={trips} tags={tags}
          affectations={items}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "zinc" }) {
  const toneBg = {
    emerald: "bg-emerald-50 border-emerald-700",
    zinc: "bg-zinc-50 border-zinc-900",
    amber: "bg-[#FFF7D6] border-zinc-900",
  }[tone] || "bg-zinc-50 border-zinc-900";
  return (
    <div className={`border ${toneBg} p-3 flex items-center gap-3`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={20} className="text-zinc-700 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 truncate">{label}</div>
        <div className="font-display font-black text-2xl leading-none">{value}</div>
      </div>
    </div>
  );
}

function AffectationForm({ affectation, vehicles, drivers, trips, tags, affectations = [], onClose, onSaved }) {
  const isEdit = !!affectation;
  const [vehicleId, setVehicleId] = useState(affectation?.vehicle_id || "");
  const [tripId, setTripId] = useState(affectation?.trip_id || "");
  const [driverId, setDriverId] = useState(affectation?.driver_id || "");
  const [bluetoothTagId, setBluetoothTagId] = useState(affectation?.bluetooth_tag_id || "");
  const [status, setStatus] = useState(affectation?.status || "active");
  const [startDate, setStartDate] = useState(affectation?.start_date?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  // Build "already-used" maps from currently active affectations.
  // When editing, the affectation being edited is excluded so its own values
  // are not flagged as conflicting.
  const usage = useMemo(() => {
    const v = new window.Map(), t = new window.Map(), d = new window.Map(), b = new window.Map();
    affectations
      .filter((a) => a.status === "active" && a.id !== affectation?.id)
      .forEach((a) => {
        if (a.vehicle_id) v.set(a.vehicle_id, a);
        if (a.trip_id) t.set(a.trip_id, a);
        if (a.driver_id) d.set(a.driver_id, a);
        if (a.bluetooth_tag_id) b.set(a.bluetooth_tag_id, a);
      });
    return { v, t, d, b };
  }, [affectations, affectation?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicleId || !tripId) { toast.error("Bus et ligne obligatoires"); return; }
    setSaving(true);
    try {
      const payload = {
        vehicle_id: vehicleId,
        trip_id: tripId,
        driver_id: driverId || null,
        bluetooth_tag_id: bluetoothTagId || null,
        status,
        start_date: startDate ? new Date(startDate).toISOString() : null,
      };
      if (isEdit) {
        await api.put(`/logibus/affectations/${affectation.id}`, payload);
        toast.success("Affectation mise à jour");
      } else {
        await api.post("/logibus/affectations", payload);
        toast.success("Affectation créée");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="affectation-form-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-2xl my-8" data-testid="affectation-form">
        <div className="border-b border-zinc-900 p-4 flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tight uppercase">
            {isEdit ? "Modifier l'affectation" : "Nouvelle affectation"}
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center justify-around text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1"><Bus size={12} /> Bus</span>
            <ArrowRight size={12} />
            <span className="flex items-center gap-1"><Map size={12} /> Ligne</span>
            <ArrowRight size={12} />
            <span className="flex items-center gap-1"><User size={12} /> Chauffeur</span>
            <ArrowRight size={12} />
            <span className="flex items-center gap-1"><Bluetooth size={12} /> Tag</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Bus *">
              <select required data-testid="select-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="">— Choisir —</option>
                {vehicles.map((v) => {
                  const taken = usage.v.get(v.id);
                  return (
                    <option key={v.id} value={v.id} disabled={!!taken && v.id !== vehicleId}
                      style={taken ? { color: "#9ca3af", textDecoration: "line-through" } : undefined}>
                      {v.plate} · {v.model}{v.navixy_tracker_id ? (String(v.navixy_tracker_id).startsWith("sim-") ? " · Traceur simulé" : " · GPS lié") : " · sans traceur"}
                      {taken ? `   ⚠ déjà affecté à ${taken.trip?.route_short_name || taken.trip_id?.slice(0, 8)}` : ""}
                    </option>
                  );
                })}
              </select>
              {usage.v.has(vehicleId) && vehicleId !== affectation?.vehicle_id && (
                <span className="text-[10px] font-mono text-red-700 mt-1 block">⚠ Ce bus est déjà affecté à une autre ligne active.</span>
              )}
            </Field>
            <Field label="Ligne (GTFS) *">
              <select required data-testid="select-trip" value={tripId} onChange={(e) => setTripId(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="">— Choisir —</option>
                {trips.map((t) => {
                  const taken = usage.t.get(t.trip_id);
                  return (
                    <option key={t.trip_id} value={t.trip_id} disabled={!!taken && t.trip_id !== tripId}
                      style={taken ? { color: "#9ca3af", textDecoration: "line-through" } : undefined}>
                      {t.route_short_name} → {t.trip_headsign} · {String(t.departure_time || "").slice(0, 5)}
                      {taken ? `   ⚠ déjà couverte par ${taken.vehicle?.plate || "un bus"}` : ""}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="Chauffeur">
              <select data-testid="select-driver" value={driverId} onChange={(e) => setDriverId(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="">— Non assigné —</option>
                {drivers.map((d) => {
                  const taken = usage.d.get(d.id);
                  return (
                    <option key={d.id} value={d.id} disabled={!!taken && d.id !== driverId}
                      style={taken ? { color: "#9ca3af", textDecoration: "line-through" } : undefined}>
                      {d.name}{taken ? `   ⚠ déjà sur ${taken.trip?.route_short_name || "une autre ligne"}` : ""}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="Badge chauffeur (Bluetooth)">
              <select data-testid="select-tag" value={bluetoothTagId} onChange={(e) => setBluetoothTagId(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="">— Aucun —</option>
                {tags.filter((t) => t.tag_type === "driver").map((t) => {
                  const taken = usage.b.get(t.id);
                  return (
                    <option key={t.id} value={t.id} disabled={!!taken && t.id !== bluetoothTagId}
                      style={taken ? { color: "#9ca3af", textDecoration: "line-through" } : undefined}>
                      {t.name}{t.driver?.name ? ` · ${t.driver.name}` : ""}
                      {taken ? `   ⚠ déjà utilisé` : ""}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="Date de début">
              <input type="date" data-testid="input-start" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm" />
            </Field>
            <Field label="Statut">
              <select data-testid="select-status" value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white">
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="completed">Terminée</option>
              </select>
            </Field>
          </div>

          <p className="text-[10px] font-mono text-zinc-500">
            ℹ️ Si une affectation active existe déjà pour ce bus, ce chauffeur ou ce tag, l'enregistrement sera refusé.
          </p>
        </div>

        <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100">
            Annuler
          </button>
          <button type="submit" disabled={saving} data-testid="submit-affectation"
            className="bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin inline" /> : null} {isEdit ? "Enregistrer" : "Créer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
